<!--
  @component SetupChecklist — Overview onboarding card.
  tier:      primitive (visual atom)
  purpose:   guide a (often non-technical) user through getting their
             app live — deploy, add a domain, set up data, invite the
             team. Each step links to where it's done. The card retires
             itself once every step is complete.
-->
<script lang="ts" module>
  export interface SetupStep {
    id: string;
    label: string;
    hint?: string;
    done: boolean;
    actionLabel?: string;
    onAction?: () => void;
  }
</script>

<script lang="ts">
  interface Props {
    steps: SetupStep[];
    title?: string;
  }
  let { steps, title = "Finish setting up" }: Props = $props();

  const doneCount = $derived(steps.filter((s) => s.done).length);
  const allDone = $derived(steps.length > 0 && doneCount === steps.length);
</script>

{#if !allDone}
  <section class="checklist">
    <header>
      <h2>{title}</h2>
      <span class="progress">{doneCount} of {steps.length} done</span>
    </header>
    <div class="track" aria-hidden="true">
      <div class="bar" style="width: {steps.length ? (doneCount / steps.length) * 100 : 0}%"></div>
    </div>
    <ul class="steps">
      {#each steps as s (s.id)}
        <li class="step" class:done={s.done}>
          <span class="check" aria-hidden="true">
            {#if s.done}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            {/if}
          </span>
          <div class="step-body">
            <p class="step-label">{s.label}</p>
            {#if s.hint}<p class="step-hint">{s.hint}</p>{/if}
          </div>
          {#if !s.done && s.actionLabel && s.onAction}
            <button type="button" class="step-action" onclick={s.onAction}>{s.actionLabel}</button>
          {/if}
        </li>
      {/each}
    </ul>
  </section>
{/if}

<style>
  .checklist {
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-lg);
    padding: var(--space-5);
    font-family: var(--type-font);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  header { display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-3); }
  h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .progress { font-size: 12px; color: var(--text-tertiary); }
  .track { height: 3px; border-radius: var(--radius-pill); background: var(--bg-hover); overflow: hidden; }
  .bar { height: 100%; background: var(--text-secondary); border-radius: var(--radius-pill); transition: width var(--ease-smooth); }
  .steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  .step {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) 0;
  }
  .step + .step { border-top: 0.5px solid var(--border-subtle); }
  .check {
    width: 20px; height: 20px;
    border-radius: var(--radius-pill);
    border: 1.5px solid var(--border-strong);
    display: grid; place-items: center;
    flex-shrink: 0;
    color: var(--surface-base);
  }
  .step.done .check { background: var(--success); border-color: var(--success); }
  .check svg { width: 12px; height: 12px; }
  .step-body { flex: 1; min-width: 0; }
  .step-label { margin: 0; font-size: 13.5px; color: var(--text-primary); }
  .step.done .step-label { color: var(--text-tertiary); text-decoration: line-through; }
  .step-hint { margin: 2px 0 0; font-size: 12px; color: var(--text-tertiary); }
  .step-action {
    flex-shrink: 0;
    appearance: none;
    background: var(--bg-active);
    color: var(--text-primary);
    border: 0.5px solid var(--border-strong);
    border-radius: var(--radius-md);
    padding: 6px var(--space-3);
    font: inherit;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    transition: background var(--ease-fast), border-color var(--ease-fast);
  }
  .step-action:hover { background: var(--bg-selected); border-color: var(--border-focus); }
</style>
